<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { menuItems } from '@/constants/menu-items'

const route = useRoute()
const activePath = computed(() => route.path)

const isActive = (menuLink: string) => {
  if (menuLink === '/') {
    return activePath.value === '/'
  }
  return activePath.value.startsWith(menuLink)
}

const scrollContainer = ref<HTMLElement | null>(null)
const thumbHeight = ref(0)
const thumbTop = ref(0)
const hasScroll = ref(false)

const updateThumb = () => {
  const el = scrollContainer.value
  if (!el) return

  const { scrollTop, scrollHeight, clientHeight } = el

  if (scrollHeight <= clientHeight) {
    hasScroll.value = false
    return
  }

  hasScroll.value = true
  thumbHeight.value = Math.max((clientHeight / scrollHeight) * clientHeight, 24)
  thumbTop.value = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight.value)
}

onMounted(() => {
  updateThumb()
  window.addEventListener('resize', updateThumb)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateThumb)
})
</script>

<template>
    <div class=" flex flex-col gap-4  bg-white rounded-xl py-2">
        <!-- 👉 Sidebar menu -->
        <div class="relative w-full group ">
            <div ref="scrollContainer" @scroll="updateThumb"
                class="no-native-scrollbar w-full flex flex-col  gap-0.5 px-1.5 pb-4 overflow-y-auto max-h-[calc(100vh-165px)]">
                <div v-for="(menu, index) in menuItems" :key="`menu-item-${index}`"
                    class="d-flex items-center py-2 h-10 pe-1">
                    <router-link :to="menu.link"
                        class="flex items-center gap-2 px-1 h-10  cursor-pointer text-[#1b325a] hover:bg-[#f5f7fa] rounded-lg text-[13px] font-medium"
                        :class="{
                            'bg-[#eaeef5]': isActive(menu.link)
                        }">
                        <img :src="menu.icon_url" width="22" alt="menu-icon" />
                        {{ menu.title }}
                    </router-link>
                </div>
            </div>

            <div v-if="hasScroll" class="custom-thumb" :style="{
                height: thumbHeight + 'px',
                transform: `translateY(${thumbTop}px)`
            }" />
        </div>
    </div>
</template>

<style scoped>
.no-native-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.no-native-scrollbar::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
}

.custom-thumb {
    position: absolute;
    top: 0;
    inset-inline-end: 2px;
    width: 3px;
    border-radius: 999px;
    background-color: rgba(27, 50, 90, 0.25);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.group:hover .custom-thumb {
    opacity: 1;
}
</style>